import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import {
  Button,
  ConfigProvider,
  Drawer,
  Grid,
  Layout,
  Menu,
  Spin,
} from "antd";
import {
  BarChartOutlined,
  KeyOutlined,
  MenuOutlined,
  RobotOutlined,
  UserOutlined,
} from "@ant-design/icons";

import PersonaTable from "./components/persona";
import GptModelForm from "./components/gptModels/updateGptModel";
import PasscodeAssignments from "./components/passcodes";
import UsageAnalytics from "./components/usageAnalytics";
import AdminLogin from "./components/AdminLogin";
import "./App.css";

const { Content, Header, Sider } = Layout;
const { useBreakpoint } = Grid;

const menuItems = [
  {
    key: "passcodes",
    icon: <KeyOutlined />,
    label: <Link to="/passcodes">Passcodes</Link>,
  },
  {
    key: "models",
    icon: <RobotOutlined />,
    label: <Link to="/models">Settings</Link>,
  },
  {
    key: "persona",
    icon: <UserOutlined />,
    label: <Link to="/persona">Personas</Link>,
  },
  {
    key: "usage",
    icon: <BarChartOutlined />,
    label: <Link to="/usage">Usage Analytics</Link>,
  },
];

function App() {
  const [isAdmin, setIsAdmin] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const currentPath = location.pathname;

  useEffect(() => {
    fetch("/api/admin/me", { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setIsAdmin(data.isAdmin))
      .catch(() => setIsAdmin(false));
  }, []);

  if (isAdmin === null) {
    return (
      <div className="app-loading">
        <Spin size="large" />
        <span>Loading admin portal...</span>
      </div>
    );
  }

  if (!isAdmin) {
    return <AdminLogin />;
  }

  const selectedKey = currentPath.startsWith("/usage")
    ? "usage"
    : currentPath.startsWith("/models")
      ? "models"
      : currentPath.startsWith("/passcodes")
        ? "passcodes"
        : "persona";

  return (
    <Layout className="admin-shell">
      {!isMobile && (
        <Sider
          width={248}
          collapsible
          collapsed={sidebarCollapsed}
          onCollapse={setSidebarCollapsed}
          className="admin-sider"
        >
          <div
            className={`admin-brand ${
              sidebarCollapsed ? "admin-brand-collapsed" : ""
            }`}
          >
            <img src="/logo-small.png" alt="" className="admin-brand-mark" />

            {!sidebarCollapsed && (
              <div>
                <div className="admin-brand-name">Comfort Companions</div>
                <div className="admin-brand-subtitle">Admin portal</div>
              </div>
            )}
          </div>

          <Menu
            theme="dark"
            selectedKeys={[selectedKey]}
            mode="inline"
            items={menuItems}
            className="admin-menu"
          />
        </Sider>
      )}

      <Layout className="admin-main-layout">
        {isMobile && (
          <Header className="mobile-admin-header">
            <Button
              type="text"
              icon={<MenuOutlined />}
              className="mobile-menu-button"
              aria-label="Open admin navigation"
              onClick={() => setMobileNavOpen(true)}
            />

            <div className="mobile-admin-brand">
              <img src="/logo-small.png" alt="" />
              <span>Admin</span>
            </div>

            <div className="mobile-header-spacer" />
          </Header>
        )}

        <Content className="admin-content">
          <div className="admin-content-card">
            <Routes>
              <Route path="/" element={<Navigate to="/passcodes" />} />
              <Route path="/persona" element={<PersonaTable />} />
              <Route path="/models" element={<GptModelForm />} />
              <Route path="/passcodes" element={<PasscodeAssignments />} />
              <Route path="/usage" element={<UsageAnalytics />} />
            </Routes>
          </div>
        </Content>
      </Layout>

      <Drawer
        title="Comfort Companions"
        placement="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        width="min(84vw, 320px)"
        className="mobile-nav-drawer"
      >
        <Menu
          selectedKeys={[selectedKey]}
          mode="inline"
          items={menuItems}
          onClick={() => setMobileNavOpen(false)}
          className="mobile-nav-menu"
        />
      </Drawer>
    </Layout>
  );
}

function AppWrapper() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#16858c",
          colorInfo: "#16858c",
          colorText: "#243443",
          colorBgLayout: "#f3f6f7",
          borderRadius: 12,
          fontSize: 16,
          controlHeight: 42,
        },
        components: {
          Button: {
            fontWeight: 600,
          },
          Card: {
            borderRadiusLG: 16,
          },
          Modal: {
            borderRadiusLG: 18,
          },
        },
      }}
    >
      <Router>
        <App />
      </Router>
    </ConfigProvider>
  );
}

export default AppWrapper;